import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthFetch } from '../hooks/useAuthFetch';
import {
  SearchIcon,
  RefreshIcon,
  PlusIcon,
  ServerIcon,
  CheckCircleIcon,
  AlertIcon,
  KeyIcon,
  ActivityIcon,
  CodeIcon,
  BookOpenIcon
} from '../components/common/Icons';

// Types for OpenAPI specs
interface APISpec {
  id: string;
  title: string;
  version: string;
  description: string;
  endpoint_count: number;
  last_updated: string;
}

interface APIEndpoint {
  path: string;
  method: string;
  summary: string;
  description: string;
  tags: string[];
  operationId: string;
}

// Types for external API endpoints (existing functionality)
interface ExternalAPIEndpoint {
  id: string;
  name: string;
  url: string;
  type: string;
  status: 'healthy' | 'unhealthy' | 'unknown';
  latency_ms: number;
  last_checked: string;
}

interface APIStats {
  total_requests: number;
  success_rate: number;
  avg_latency: number;
  active_keys: number;
}

type TabType = 'specs' | 'external';

const APIPage: React.FC = () => {
  const { isLoading: authLoading } = useAuth();
  const { authFetch, cancelPendingRequests, isCancelled } = useAuthFetch();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('specs');

  // Debounce ref for tab changes
  const tabChangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // OpenAPI specs state
  const [specs, setSpecs] = useState<APISpec[]>([]);
  const [selectedSpec, setSelectedSpec] = useState<string | null>(null);
  const [specEndpoints, setSpecEndpoints] = useState<APIEndpoint[]>([]);
  const [specsLoading, setSpecsLoading] = useState(true);
  const [specsError, setSpecsError] = useState<string | null>(null);

  // External APIs state (existing)
  const [externalEndpoints, setExternalEndpoints] = useState<ExternalAPIEndpoint[]>([]);
  const [stats, setStats] = useState<APIStats | null>(null);
  const [externalLoading, setExternalLoading] = useState(false);
  const [externalError, setExternalError] = useState<string | null>(null);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Define fetch functions first (before useEffects that use them)
  const fetchSpecs = useCallback(async () => {
    setSpecsLoading(true);
    setSpecsError(null);
    try {
      const response = await authFetch('/api/admin/specs', { requestKey: 'fetch-specs' });

      // Check if request was cancelled (user navigated away)
      if (isCancelled(response)) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setSpecs(Array.isArray(data) ? data : []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSpecsError(errorData.detail || 'Failed to fetch API specifications');
      }
    } catch (err) {
      // Don't show error for cancelled requests
      if (err instanceof Error && err.message === 'Request cancelled') {
        return;
      }
      setSpecsError('Failed to connect to API');
    }
    setSpecsLoading(false);
  }, [authFetch, isCancelled]);

  const fetchSpecEndpoints = useCallback(async (specId: string) => {
    try {
      const response = await authFetch(`/api/admin/specs/${specId}/endpoints`, {
        requestKey: `fetch-spec-endpoints-${specId}`
      });

      // Check if request was cancelled
      if (isCancelled(response)) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setSpecEndpoints(Array.isArray(data) ? data : []);
        setSelectedSpec(specId);
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'Request cancelled') {
        return;
      }
      console.error('Failed to fetch spec endpoints:', err);
    }
  }, [authFetch, isCancelled]);

  const fetchExternalAPIs = useCallback(async () => {
    setExternalLoading(true);
    setExternalError(null);
    try {
      const [endpointsRes, statsRes] = await Promise.all([
        authFetch('/api/admin/apis/endpoints', { requestKey: 'fetch-external-endpoints' }),
        authFetch('/api/admin/apis/dashboard', { requestKey: 'fetch-external-dashboard' })
      ]);

      // Check if requests were cancelled
      if (isCancelled(endpointsRes) || isCancelled(statsRes)) {
        return;
      }

      if (endpointsRes.ok) {
        const data = await endpointsRes.json();
        setExternalEndpoints(Array.isArray(data) ? data : data.endpoints || []);
      } else {
        const errorData = await endpointsRes.json().catch(() => ({}));
        setExternalError(errorData.detail || 'Failed to fetch API endpoints');
      }

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'Request cancelled') {
        return;
      }
      setExternalError('Failed to connect to API');
    }
    setExternalLoading(false);
  }, [authFetch, isCancelled]);

  // Debounced tab change to prevent rapid fire
  const handleTabChange = useCallback((tab: TabType) => {
    // Clear any pending tab change
    if (tabChangeTimeoutRef.current) {
      clearTimeout(tabChangeTimeoutRef.current);
    }

    // Cancel pending requests immediately
    cancelPendingRequests();

    // Debounce the actual tab change
    tabChangeTimeoutRef.current = setTimeout(() => {
      setActiveTab(tab);
    }, 100);
  }, [cancelPendingRequests]);

  // Cancel pending requests when tab changes or component unmounts
  useEffect(() => {
    return () => {
      cancelPendingRequests();
      if (tabChangeTimeoutRef.current) {
        clearTimeout(tabChangeTimeoutRef.current);
      }
    };
  }, [cancelPendingRequests]);

  // Load OpenAPI specs on mount
  useEffect(() => {
    if (authLoading) return;
    fetchSpecs();
  }, [authLoading, fetchSpecs]);

  // Load external APIs when tab changes
  useEffect(() => {
    if (authLoading) return;
    if (activeTab === 'external' && externalEndpoints.length === 0) {
      fetchExternalAPIs();
    }
  }, [activeTab, authLoading, externalEndpoints.length, fetchExternalAPIs]);

  const reloadSpecs = async () => {
    try {
      await authFetch('/api/admin/specs/reload', { method: 'POST' });
      await fetchSpecs();
      setSelectedSpec(null);
      setSpecEndpoints([]);
    } catch (err) {
      console.error('Failed to reload specs:', err);
    }
  };

  const getMethodClass = (method: string) => {
    const classes: Record<string, string> = {
      GET: 'method-get',
      POST: 'method-post',
      PUT: 'method-put',
      PATCH: 'method-patch',
      DELETE: 'method-delete'
    };
    return classes[method] || '';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon size={16} style={{ color: 'var(--success)' }} />;
      case 'unhealthy': return <AlertIcon size={16} style={{ color: 'var(--error)' }} />;
      default: return <AlertIcon size={16} style={{ color: 'var(--warning)' }} />;
    }
  };

  // Filter endpoints based on search
  const filteredSpecEndpoints = specEndpoints.filter(ep =>
    ep.path.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ep.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ep.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Group endpoints by their first tag
  const groupEndpointsByTag = (endpoints: APIEndpoint[]) => {
    const groups: Record<string, APIEndpoint[]> = {};
    endpoints.forEach(ep => {
      const tag = ep.tags[0] || 'Other';
      if (!groups[tag]) groups[tag] = [];
      groups[tag].push(ep);
    });
    return groups;
  };

  const filteredExternalEndpoints = externalEndpoints.filter(ep =>
    ep.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ep.url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render OpenAPI Specs tab
  const renderSpecsTab = () => (
    <>
      {specsError && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {specsError}
        </div>
      )}

      {specsLoading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading API specifications...</p>
        </div>
      ) : specs.length === 0 ? (
        <div className="glass-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <BookOpenIcon size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
          <h3 style={{ margin: '0 0 0.5rem' }}>No API Specifications Found</h3>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
            Create OpenAPI specifications in <code style={{
              background: 'var(--bg-secondary)',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px'
            }}>platform-api/openapi/</code> to see them listed here.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          {/* Spec List */}
          <div>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem' }}>
              Registered APIs ({specs.length})
            </h3>
            {specs.map(spec => (
              <div
                key={spec.id}
                className={`glass-card ${selectedSpec === spec.id ? 'selected' : ''}`}
                onClick={() => fetchSpecEndpoints(spec.id)}
                style={{
                  marginBottom: '0.75rem',
                  cursor: 'pointer',
                  padding: '1.25rem',
                  transition: 'all 0.2s ease',
                  border: selectedSpec === spec.id ? '2px solid var(--accent-primary)' : '2px solid transparent'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.25rem', fontSize: '1rem' }}>{spec.title}</h4>
                    <p style={{
                      margin: '0 0 0.5rem',
                      fontSize: '0.85rem',
                      color: 'var(--text-secondary)',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {spec.description?.split('\n')[0] || 'No description'}
                    </p>
                  </div>
                  <span style={{
                    background: 'var(--accent-primary)',
                    color: 'white',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    v{spec.version}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '0.8rem',
                  color: 'var(--text-muted)'
                }}>
                  <span>{spec.endpoint_count} endpoints</span>
                  <span>Updated: {new Date(spec.last_updated).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Endpoints List */}
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                {selectedSpec
                  ? `Endpoints - ${specs.find(s => s.id === selectedSpec)?.title}`
                  : 'Select an API to view endpoints'}
              </h3>
              {selectedSpec && (
                <a
                  href="/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost btn-sm"
                >
                  <CodeIcon size={14} /> Swagger UI
                </a>
              )}
            </div>

            {selectedSpec && specEndpoints.length > 0 ? (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {Object.entries(groupEndpointsByTag(filteredSpecEndpoints)).map(([tag, endpoints]) => (
                  <div key={tag} className="glass-card" style={{ marginBottom: '0.75rem', padding: '1rem' }}>
                    <h4 style={{
                      margin: '0 0 0.75rem',
                      fontSize: '0.95rem',
                      color: 'var(--accent-primary)',
                      borderBottom: '1px solid var(--border-color)',
                      paddingBottom: '0.5rem'
                    }}>
                      {tag} <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.8rem' }}>
                        ({endpoints.length} endpoints)
                      </span>
                    </h4>
                    {endpoints.map((ep, idx) => (
                      <div
                        key={`${ep.method}-${ep.path}-${idx}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.5rem 0',
                          borderBottom: idx < endpoints.length - 1 ? '1px solid var(--border-color)' : 'none'
                        }}
                      >
                        <span
                          className={`method-badge ${getMethodClass(ep.method)}`}
                          style={{
                            padding: '0.2rem 0.4rem',
                            borderRadius: '4px',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            minWidth: '50px',
                            textAlign: 'center'
                          }}
                        >
                          {ep.method}
                        </span>
                        <code style={{
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          flexShrink: 0
                        }}>
                          {ep.path}
                        </code>
                        <span style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {ep.summary}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : selectedSpec ? (
              <div className="glass-card" style={{ textAlign: 'center', padding: '2rem' }}>
                <p style={{ color: 'var(--text-secondary)', margin: 0 }}>No endpoints defined</p>
              </div>
            ) : (
              <div className="glass-card" style={{ textAlign: 'center', padding: '2rem' }}>
                <CodeIcon size={32} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
                <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                  Click on an API specification to view its endpoints
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Method badge styles */}
      <style>{`
        .method-get { background: #61affe; color: white; }
        .method-post { background: #49cc90; color: white; }
        .method-put { background: #fca130; color: white; }
        .method-patch { background: #50e3c2; color: white; }
        .method-delete { background: #f93e3e; color: white; }
      `}</style>
    </>
  );

  // Render External APIs tab (existing functionality)
  const renderExternalTab = () => (
    <>
      {externalError && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {externalError}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="stat-card">
            <div className="stat-icon">
              <ActivityIcon size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{(stats.total_requests / 1000).toFixed(1)}K</span>
              <span className="stat-label">Total Requests</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">
              <CheckCircleIcon size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.success_rate}%</span>
              <span className="stat-label">Success Rate</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <ServerIcon size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.avg_latency}ms</span>
              <span className="stat-label">Avg Latency</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <KeyIcon size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.active_keys}</span>
              <span className="stat-label">Active API Keys</span>
            </div>
          </div>
        </div>
      )}

      <div className="glass-card">
        {externalLoading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading API endpoints...</p>
          </div>
        ) : filteredExternalEndpoints.length === 0 ? (
          <div className="empty-state">
            <ServerIcon size={48} />
            <p>No external API endpoints configured</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>URL</th>
                <th>Type</th>
                <th>Status</th>
                <th>Latency</th>
                <th>Last Checked</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExternalEndpoints.map((endpoint) => (
                <tr key={endpoint.id}>
                  <td className="font-medium">{endpoint.name}</td>
                  <td className="font-mono text-sm text-secondary">{endpoint.url}</td>
                  <td>
                    <span className="badge badge-secondary">{endpoint.type}</span>
                  </td>
                  <td>
                    <span className="status-indicator">
                      {getStatusIcon(endpoint.status)}
                      <span className="capitalize">{endpoint.status}</span>
                    </span>
                  </td>
                  <td>{endpoint.latency_ms}ms</td>
                  <td className="text-secondary">{endpoint.last_checked}</td>
                  <td>
                    <button className="btn btn-ghost btn-sm">Test</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">API Management</h1>
          <p className="page-subtitle">
            {activeTab === 'specs'
              ? 'Registered OpenAPI specifications and endpoints'
              : 'External API integrations and health monitoring'}
          </p>
        </div>
        <div className="page-header-right">
          {activeTab === 'specs' ? (
            <button className="btn btn-ghost" onClick={reloadSpecs} title="Reload specifications">
              <RefreshIcon size={18} />
            </button>
          ) : (
            <>
              <button className="btn btn-primary">
                <PlusIcon size={16} />
                Add Endpoint
              </button>
              <button className="btn btn-ghost" onClick={fetchExternalAPIs}>
                <RefreshIcon size={18} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        borderBottom: '1px solid var(--border-color)',
        paddingBottom: '0.5rem'
      }}>
        <button
          className={`btn ${activeTab === 'specs' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => handleTabChange('specs')}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <BookOpenIcon size={16} />
          OpenAPI Specs
        </button>
        <button
          className={`btn ${activeTab === 'external' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => handleTabChange('external')}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <ServerIcon size={16} />
          External APIs
        </button>
      </div>

      {/* Search bar for endpoints */}
      {((activeTab === 'specs' && selectedSpec) || activeTab === 'external') && (
        <div className="page-toolbar" style={{ marginBottom: '1rem' }}>
          <div className="input-wrapper" style={{ maxWidth: '320px' }}>
            <SearchIcon size={18} className="input-icon" />
            <input
              type="text"
              className="input input-with-icon"
              placeholder={activeTab === 'specs' ? 'Search endpoints...' : 'Search external APIs...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'specs' ? renderSpecsTab() : renderExternalTab()}
    </div>
  );
};

export default APIPage;
